import { Modal, ModalBody, Text, useTranslation } from "@familyzone/component-library";
import React, { useEffect, useState } from "react";
import { StudentActionEvent, StudentActionType } from "../../types/Community";

interface Props {
  event: StudentActionEvent;
  open: boolean;
  onClose: () => void;
  onConfirm: (event: StudentActionEvent) => void;
}

const headerTextMap = {
  [StudentActionType.DisconnectStudentFromParent]: "Restrict Student",
  [StudentActionType.DisconnectParentFromStudent]: "Disconnect Parent",
  [StudentActionType.UnlinkStudentFromParent]: "Restrict Student",
  [StudentActionType.UnlinkParentFromStudent]: "Remove Parent",
  [StudentActionType.UnRestrictStudent]: "Unrestrict Student",
};

const actionTextMap = {
  [StudentActionType.DisconnectStudentFromParent]: "Restrict",
  [StudentActionType.DisconnectParentFromStudent]: "Disconnect",
  [StudentActionType.UnlinkStudentFromParent]: "Restrict",
  [StudentActionType.UnlinkParentFromStudent]: "Remove",
  [StudentActionType.UnRestrictStudent]: "Unrestrict",
};

const savingActionTextMap = {
  [StudentActionType.DisconnectStudentFromParent]: "Restricting...",
  [StudentActionType.DisconnectParentFromStudent]: "Disconnecting...",
  [StudentActionType.UnlinkStudentFromParent]: "Restricting...",
  [StudentActionType.UnlinkParentFromStudent]: "Removing...",
  [StudentActionType.UnRestrictStudent]: "Unrestricting...",
};

const ParentDisconnectModal: React.FC<Props> = ({ event, open, onClose, onConfirm }: Props) => {
  const { t } = useTranslation();

  const [saving, setSaving] = useState<boolean>(false);

  const handleConfirm = (): void => {
    setSaving(true);
    onConfirm(event);
  };

  useEffect(() => {
    if (open) {
      setSaving(false);
    }
  }, [open]);

  const studentName = event.student.firstName + " " + event.student.lastName;
  const guardianName = event.guardian.firstName + " " + event.guardian.lastName;

  return (
    <Modal
      isOpen={open}
      onClose={onClose}
      variant="warning"
      size="md"
      headerText={t(headerTextMap[event.type])}
      primaryCTALabel={saving ? t(savingActionTextMap[event.type]) : t(actionTextMap[event.type])}
      primaryCTADisabled={saving}
      onPrimaryCTAClick={handleConfirm}
      secondaryCTALabel={t("Cancel")}
      secondaryCTADisabled={saving}
      onSecondaryCTAClick={onClose}
    >
      <ModalBody>
        <>
          {event.type === StudentActionType.DisconnectParentFromStudent && (
            <>
              <Text mb="sp24">
                {t("Disconnecting a parent from a student means the parent will not be able to see the student school time activity.")}
              </Text>
              {event.guardian.delegated && <Text mb="sp24">{t("It will also disable Extended Rules.")}</Text>}
              <Text data-testid="confirmation-question">
                {t("Are you sure you want to disconnect parent")} <b>{guardianName}</b> {t("from student")} <b>{studentName}</b>?
              </Text>
            </>
          )}

          {event.type === StudentActionType.UnlinkParentFromStudent && (
            <>
              <Text mb="sp24">{t("This will remove the parent from the student completely.")}</Text>
              <Text data-testid="confirmation-question">
                {t("Are you sure you want to remove parent")} <b>{guardianName}</b> {t("from student")} <b>{studentName}</b>?
              </Text>
            </>
          )}

          {event.type === StudentActionType.DisconnectStudentFromParent && (
            <>
              <Text mb="sp24">
                {t(
                  "This will prevent the parent from connecting to this student through the Qustodio app and viewing new online activity."
                )}
              </Text>
              {event.student.delegated && <Text mb="sp24">{t("It will also disable Extended Rules.")}</Text>}
              <Text data-testid="confirmation-question">
                {t("Are you sure you want to restrict student")} <b>{studentName}</b> {t("from being connected to parent")}{" "}
                <b>{guardianName}</b>?
              </Text>
            </>
          )}

          {event.type === StudentActionType.UnlinkStudentFromParent && (
            <>
              <Text mb="sp24">
                {t(
                  "This will prevent the parent from connecting to this student through the Qustodio app and viewing new online activity."
                )}
              </Text>
              <Text data-testid="confirmation-question">
                {t("Are you sure you want to restrict student")} <b>{studentName}</b> {t("from being connected to parent")}{" "}
                <b>{guardianName}</b>?
              </Text>
            </>
          )}

          {event.type === StudentActionType.UnRestrictStudent && (
            <>
              <Text mb="sp24">{t("This will allow the parent to connect to the student in the Qustodio App.")}</Text>
              <Text data-testid="confirmation-question">
                {t("Are you sure you want to unrestrict student")} <b>{studentName}</b>?
              </Text>
            </>
          )}
        </>
      </ModalBody>
    </Modal>
  );
};

export default ParentDisconnectModal;
